// Apex Business Intelligence — Round 17 (Chat Now) — additive standalone module
import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem("apex_auth_token") || "";
async function j(p, init = {}) {
  const r = await fetch(`${API}${p}`, { ...init, headers: { ...(init.headers || {}), "Content-Type": "application/json", Authorization: "Bearer " + tok() } });
  if (!r.ok) { let m=""; try{m=(await r.json()).detail||"";}catch{} throw new Error(m || `HTTP ${r.status}`); }
  return r.json();
}

// Singleton presence heartbeat — starts when user logs in, runs every 30 s.
let _hbTimer = null;
export function startChatPresence() {
  if (typeof window === "undefined" || _hbTimer) return;
  const beat = () => { if (!tok()) return; fetch(`${API}/api/iv/r17/chat/heartbeat`, { method: "POST", headers: { Authorization: "Bearer " + tok() } }).catch(() => {}); };
  beat();
  _hbTimer = setInterval(beat, 30000);
  // Heartbeat on focus too so "Active" status updates fast after returning to tab
  window.addEventListener("focus", beat);
}

// Hook used by the sidebar to display the unread badge.
export function useChatUnread() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const tick = () => { if (!tok()) return; fetch(`${API}/api/iv/r17/chat/unread`, { headers: { Authorization: "Bearer " + tok() } }).then(r => r.ok ? r.json() : null).then(d => { if (d) setN(d.total_unread || 0); }).catch(() => {}); };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, []);
  return n;
}

export function ChatNowPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);     // email of selected peer
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Periodic load of users + presence + unread counts (every 5 s)
  const loadUsers = useCallback(async () => {
    try { const d = await j(`/api/iv/r17/chat/users`); setUsers(d.users || []); }
    catch { setUsers([]); }
  }, []);
  useEffect(() => { loadUsers(); const t = setInterval(loadUsers, 5000); return () => clearInterval(t); }, [loadUsers]);

  // Poll messages for active conversation every 3 s
  const loadMsgs = useCallback(async () => {
    if (!active) { setMessages([]); return; }
    try {
      const d = await j(`/api/iv/r17/chat/messages?with_=${encodeURIComponent(active)}`);
      setMessages(d.messages || []);
      // Mark all newly visible incoming msgs as read
      await j(`/api/iv/r17/chat/read`, { method: "POST", body: JSON.stringify({ with_email: active }) }).catch(() => {});
      // Refresh user list so unread counts reset on the left panel
      loadUsers();
    } catch {}
  }, [active, loadUsers]);
  useEffect(() => { loadMsgs(); if (!active) return; const t = setInterval(loadMsgs, 3000); return () => clearInterval(t); }, [loadMsgs, active]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    if (!active || !text.trim()) return;
    setBusy(true);
    try {
      await j(`/api/iv/r17/chat/messages`, { method: "POST", body: JSON.stringify({ to_email: active, text: text.trim() }) });
      setText("");
      loadMsgs();
    } catch (e) { toast(e.message || "Send failed", "err"); }
    finally { setBusy(false); }
  };

  const meEmail = (currentUser?.email || "").toLowerCase();
  const peer = users.find(u => u.email === active);

  const dotColor = (s) => s === "active" ? "#16a34a" : s === "away" ? "#facc15" : "#9ca3af";

  return (
    <div data-testid="r17-chat-now" style={{ display: "flex", height: "calc(100vh - 110px)", margin: 14, background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden", color: "#1a1a1a" }}>
      {/* LEFT: users */}
      <div style={{ width: 280, borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
          <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 16, fontWeight: 700, color: "#1a1a1a", letterSpacing: 1 }}>CHAT NOW</div>
          <div style={{ fontSize: 10, color: "#666", fontWeight: 600 }}>Direct messages within your company</div>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {users.length === 0 && <div style={{ padding: 14, fontSize: 11, color: "#666", fontWeight: 600 }}>No other users in your company yet.</div>}
          {users.map(u => (
            <div key={u.email} data-testid={`r17-user-${u.email}`} onClick={() => setActive(u.email)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
                       background: active === u.email ? "#fef2f2" : (u.unread > 0 ? "#fffbeb" : "#fff") }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor(u.status), border: "1px solid #fff", boxShadow: "0 0 0 1px " + dotColor(u.status) }} title={u.status}></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: u.unread > 0 ? 800 : 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
                <div style={{ fontSize: 10, color: "#666", fontWeight: 600, textTransform: "capitalize" }}>{u.status}</div>
              </div>
              {u.unread > 0 && <span data-testid={`r17-unread-${u.email}`} style={{ background: "#C0392B", color: "#fff", fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 10, minWidth: 18, textAlign: "center" }}>{u.unread}</span>}
            </div>
          ))}
        </div>
      </div>
      {/* RIGHT: conversation */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#fafafa" }}>
        {!active && <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#666", fontSize: 13, fontWeight: 600 }}>Select a user on the left to start chatting.</div>}
        {active && (
          <>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor(peer?.status || "offline") }}></div>
              <div style={{ fontFamily: "Rajdhani,sans-serif", fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{peer?.name || active}</div>
              <div style={{ fontSize: 10, color: "#666", fontWeight: 600, textTransform: "capitalize", marginLeft: 6 }}>{peer?.status || "offline"}</div>
            </div>
            <div ref={scrollRef} data-testid="r17-thread" style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
              {messages.length === 0 && <div style={{ textAlign: "center", color: "#666", fontSize: 12, fontWeight: 600 }}>No messages yet. Say hi.</div>}
              {messages.map(m => {
                const mine = m.from_email === meEmail;
                return (
                  <div key={m.id} data-testid={`r17-msg-${m.id}`} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                    <div style={{ background: mine ? "#1e40af" : "#fff", color: mine ? "#fff" : "#1a1a1a", padding: "8px 12px", borderRadius: 12, border: mine ? "none" : "1px solid #d1d5db", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, fontWeight: 500 }}>
                      {m.text}
                    </div>
                    <div style={{ fontSize: 9, color: "#666", fontWeight: 600, marginTop: 2, textAlign: mine ? "right" : "left" }}>
                      {mine ? "You" : (m.from_name || m.from_email)} · {fmtTime(m.created_at)}
                      {mine && m.read_at && <span data-testid={`r17-read-${m.id}`} style={{ marginLeft: 6, color: "#16a34a", fontWeight: 700 }}>· Read {fmtTime(m.read_at)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 12, borderTop: "1px solid #e5e7eb", background: "#fff", display: "flex", gap: 8 }}>
              <input data-testid="r17-input" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} placeholder={`Message ${peer?.name || ""}…`} style={{ flex: 1, padding: "10px 12px", border: "1px solid #c8c8c8", borderRadius: 4, color: "#1a1a1a", fontSize: 13, fontWeight: 500 }} />
              <button data-testid="r17-send" onClick={send} disabled={busy || !text.trim()} className="btn btn-primary" style={{ fontSize: 12, padding: "8px 20px", fontWeight: 700 }}>{busy ? "…" : "Send"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) + " · " + d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}
